import React, { useState, useEffect, useRef } from 'react';
import { api } from '../services/api';

export const AutocompleteInput = ({ 
  value, 
  onChange, 
  endpoint, 
  placeholder = '', 
  className = '',
  dataTestId = ''
}) => {
  const [allOptions, setAllOptions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);

  // Fetch all options on mount
  useEffect(() => {
    const fetchOptions = async () => {
      setLoading(true);
      try {
        const response = await api.get(endpoint);
        setAllOptions(response.data || []);
      } catch (error) {
        console.error('Error fetching autocomplete options:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchOptions();
  }, [endpoint]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Compute filtered suggestions based on current input value
  const filtered = value
    ? allOptions.filter(opt => opt.toLowerCase().includes(value.toLowerCase()))
    : allOptions;

  const handleInputChange = (e) => {
    onChange(e.target.value);
    setShowSuggestions(true);
  };

  const handleSuggestionClick = (suggestion) => {
    onChange(suggestion);
    setShowSuggestions(false);
  };

  const handleClear = () => {
    onChange('');
    setShowSuggestions(false);
  };

  const handleFocus = () => {
    setShowSuggestions(true);
  };

  return (
    <div ref={wrapperRef} className="relative">
      <input
        type="text"
        value={value}
        onChange={handleInputChange}
        onFocus={handleFocus}
        placeholder={placeholder}
        className={className}
        data-testid={dataTestId}
        autoComplete="off"
      />
      
      {showSuggestions && (
        <div 
          className="absolute z-50 w-full mt-1 bg-[#0B132B] border border-white/10 rounded-md shadow-lg max-h-60 overflow-y-auto"
          data-testid={`${dataTestId}-suggestions`}
        >
          <div
            onClick={handleClear}
            className="px-3 py-2 cursor-pointer hover:bg-white/5 text-sm text-slate-400 italic transition-colors border-b border-white/5"
            data-testid="suggestion-clear"
          >
            Tous
          </div>
          {filtered.map((suggestion, index) => (
            <div
              key={index}
              onClick={() => handleSuggestionClick(suggestion)}
              className={`px-3 py-2 cursor-pointer hover:bg-white/5 text-sm transition-colors ${
                suggestion === value ? 'text-white bg-white/5' : 'text-slate-300'
              }`}
              data-testid={`suggestion-${index}`}
            >
              {suggestion}
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="px-3 py-2 text-xs text-slate-500">
              Aucun résultat
            </div>
          )}
        </div>
      )}
      
      {loading && (
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
          <div className="animate-spin h-4 w-4 border-2 border-[#0B132B] border-t-transparent rounded-full"></div>
        </div>
      )}
    </div>
  );
};

export default AutocompleteInput;
